import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import Amplify from 'aws-amplify';
import { useDispatch, useSelector } from 'react-redux';
import messages from './messages';
import validator from './validator';
import styles from './LoginForm.module.scss';
import InputText from '../../common/InputText';
import InputPassword from '../../common/InputPassword';
import Checkbox from '../../common/Checkbox';
import AccountsService from '../../../services/customer-accounts';
import AuthService from '../../../services/cognito/auth';
import drupalPubSubService from '../../../services/drupalPubSub/drupalPubSub';

import {
  getActiveCustomer,
  setLoadResetPasswordComponent,
  setShowResetModalByDefault,
} from '../../../slices/accounts';
import awsconfig from '../../../aws-exports';

import PrimaryButton from '../../common/PrimaryButton';
import LinkButton from '../../common/LinkButton';
import ResetPasswordModal from '../../ResetPasswordModal';

const LoginForm = ({
  loginErrorCallback,
  closeModalCallback,
  setLoggedInCustomerId,
  mergeCart,
  redirectToAccountDashboard,
}) => {
  const { formatMessage } = useIntl();
  const [rememberMe, setRememberMe] = useState(true);

  const { anonymousId } = useSelector((state) => ({
    anonymousId: state.cart?.anonymousId || state.order?.anonymousId,
  }));

  const { loadResetPasswordComponent } = useSelector((state) => ({
    loadResetPasswordComponent: state.accounts?.loadResetPasswordComponent,
  }));

  const { cart } = useSelector((state) => state);

  const dispatch = useDispatch();

  Amplify.configure(awsconfig);

  const onLogin = async (values) => {
    if (rememberMe) {
      localStorage.setItem('PUA::Remembered', values.email);
    }
    try {
      const response = await AuthService.authorize({
        username: values.email,
        password: values.password,
      });
      // merge cart
      const { idToken } = response.signInUserSession;
      const { customerId } = idToken.payload;

      setLoggedInCustomerId(customerId);

      const anonymousCartSignInModes = {
        merge: 'MergeWithExistingCustomerCart',
        doNotMerge: 'UseAsNewActiveCustomerCart',
      };

      if (mergeCart) {
        AccountsService.login(
          values.email,
          anonymousId,
          anonymousCartSignInModes.merge,
        );
        drupalPubSubService.publish({
          subscription: 'onAccountSignIn',
          parameters: {},
        });
        drupalPubSubService.publish({
          subscription: 'onCartUpdate',
          parameters: {
            lineItems: cart.lineItems.map(({ id, quantity }) => ({
              id,
              quantity,
            })),
          },
        });
      } else {
        const isLogin = await AccountsService.login(
          values.email,
          anonymousId,
          anonymousCartSignInModes.doNotMerge,
        );
        drupalPubSubService.publish('onAccountSignIn', {});

        if (isLogin?.cartMerged) {
          const loggedInCustomerInfo = await AccountsService.getCTCustomer(
            customerId,
          );
          if (loggedInCustomerInfo.addresses.length <= 0) {
            dispatch(getActiveCustomer(customerId));
          }
          closeModalCallback();
        }
      }
      if (redirectToAccountDashboard) {
        window.location.replace('/account');
      } else {
        closeModalCallback();
      }
    } catch (error) {
      loginErrorCallback(error, values.email);
    }
  };

  const {
    values, errors, handleChange, handleBlur, handleSubmit, touched,
  } =
    useFormik({
      initialValues: {
        email: localStorage.getItem('PUA::Remembered') || '',
        password: '',
      },
      onSubmit: onLogin,
      validate: (v) => validator(v, { formatMessage }),
    });

  return (
    <>
      { loadResetPasswordComponent ? (
        <ResetPasswordModal
          showResetModal
          setLoggedInCustomerId={setLoggedInCustomerId}
          mergeCart
        />
      ) :
        (
          <form onSubmit={handleSubmit} data-testid="sign-in-form">
            <div className={styles.loginInputField}>
              <InputText
                id="email"
                type="email"
                name="email"
                label={formatMessage(messages.emailLabel)}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                errorMsg={touched.email ? errors.emailError : ''}
                isValid={!touched.email || !!errors?.email}
                required
              />
            </div>
            <div className={styles.loginInputField}>
              <InputPassword
                id="password"
                name="password"
                label={formatMessage(messages.passwordLabel)}
                handleOnChange={handleChange}
                handleOnBlur={handleBlur}
                value={values.password}
                errorMsg={touched.password ? errors.passwordError : undefined}
                isValid={!touched.password || !!errors?.password}
                isLogin
                required
              />
            </div>
            <div className={styles.forgotPwRow}>
              <div>
                <Checkbox
                  id="rememberMe"
                  name="rememberMe"
                  label={formatMessage(messages.rememberMeLabel)}
                  onChange={() => setRememberMe(!rememberMe)}
                  checked={rememberMe}
                />
              </div>
              <LinkButton
                id="loadResetPasswordComponent"
                onClick={() => {
                  dispatch(setShowResetModalByDefault({ showResetModalByDefault: true }));
                  dispatch(setLoadResetPasswordComponent({ loadResetPasswordComponent: true }));
                }}
                linkText={formatMessage(messages.forgotPasswordLink)}
              />
            </div>
            <PrimaryButton
              type="submit"
              title={formatMessage(messages.signInButton)}
              dataTestId="sign-in-btn"
              onClick={() => {}}
            />
          </form>
        )}
    </>
  );
};

LoginForm.propTypes = {
  loginErrorCallback: PropTypes.func,
  closeModalCallback: PropTypes.func,
  setLoggedInCustomerId: PropTypes.func.isRequired,
  mergeCart: PropTypes.bool,
  redirectToAccountDashboard: PropTypes.bool,
};

LoginForm.defaultProps = {
  loginErrorCallback: () => {},
  closeModalCallback: () => {},
  mergeCart: false,
  redirectToAccountDashboard: true,
};

export default LoginForm;
